import { useEffect, useState } from 'react';
import BkTable from '../../../table/BkTable';
import { Column } from 'src/utils/constants/table-constants';
import { TextField } from 'src/components/new/inputs/TextField';
import { Button } from 'src/components/new/Button';
import { Box, Typography } from '@mui/material';
import { apiInstance } from 'src/api/apiInstance';
import { updateJobTrackingNumbers } from 'src/features/admin/utils';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/store/store';

interface JobTrackingNumbersProps {
  isCosMember: boolean;
  jobId: number;
  updateSnackbarMsg: (params: { msg: string; type: 'error' | 'success' }) => void;
}

const MAILING_GROUPS = {
  Priority: [
    'first_class_domestic',
    'first_class_canada',
    'first_class_mexico',
    'first_class_international',
    'certified_canada',
    'certified_mexico',
    'certified_international',
  ],

  'Priority Express': [
    'priority_express_domestic',
    'priority_express_international',
    'priority_express_canada',
    'priority_express_mexico',
  ],

  Certified: [
    'certified_domestic',
  ],
};

const MAILING_NAME_BY_TYPE = Object.fromEntries(
  Object.entries(MAILING_GROUPS).flatMap(([label, types]) =>
    types.map(type => [type, label])
  )
);

const columns = [
  { headerName: 'Name', field: 'name' },
  { headerName: 'Address', field: 'address' },
  { headerName: 'Mailing Type', field: 'type' },
  { headerName: 'Tracking link', field: 'trackingNumber' },
];

const JobTrackingNumbers = ({ isCosMember, jobId, updateSnackbarMsg }: JobTrackingNumbersProps) => {
  const [data, setData] = useState<any>([]);
  const [missingTrackingNumbers, setMissingTrackingNumbers] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const dispatch = useDispatch<AppDispatch>();

  const getPrintDocuments = async () => {
    const skip = page * rowsPerPage;
    const take = rowsPerPage;
    try {
      setIsLoading(true);
      const response = await apiInstance.get(
        `/job/print-documents?skip=${skip}&take=${take}&jobId=${jobId}&pbTrackingNumber_isnot=null`,
      );
      const transformedData = response.data.items.map((document: any) => {
        return {
          name: document.recipient.name,
          address: Array.isArray(document.recipient.address) ? document.recipient.address.join(' ') : '-',
          trackingNumber: document.pbTrackingNumber,
          id: document.id,
          type:
            MAILING_NAME_BY_TYPE[document.recipient.mailingType as keyof typeof MAILING_NAME_BY_TYPE] ||
            document.recipient.mailingType,
        };
      });
      setIsLoading(false)
      setTotalCount(response.data.totalCount);
      setData(transformedData);
    } catch (err: any) {
      setIsLoading(false);
      updateSnackbarMsg({ type: 'error', msg: 'Error occured while loading tracking numbers' });
    }
  };

  useEffect(() => {
    getPrintDocuments();
  }, [page, rowsPerPage]);

  const onTrackingNumberChange = (id: string) => (e: any) => {
    setMissingTrackingNumbers({ ...missingTrackingNumbers, [id]: e.target.value });
  };

  const onReset = () => {
    setMissingTrackingNumbers({});
  };

  const onSubmit = () => {
    const preparedData = Object.keys(missingTrackingNumbers).map((key) => ({
      id: Number(key),
      pbTrackingNumber: missingTrackingNumbers[key],
    }));
    dispatch(updateJobTrackingNumbers(preparedData)).then((result) => {
      if (updateJobTrackingNumbers.fulfilled.match(result)) {
        getPrintDocuments();
        updateSnackbarMsg({ msg: 'Tracking numbers updated successfully', type: 'success' });
      }
      if (updateJobTrackingNumbers.rejected.match(result)) {
        updateSnackbarMsg({ msg: 'Could not update job tracking numbers', type: 'error' });
      }
    });
  };

  if (!data.length) return null;

  const renderCell = (row: any, column: Column) => {
    switch (column.field) {
      case 'trackingNumber':
        return renderTrackingLink(row);
      default:
        return row[column.field];
    }
  };

  const renderTrackingLink = (row: any) => {
    if (row.trackingNumber === '$$ERROR$$' && isCosMember) {
      return (
        <TextField
          id={`missing_tracking_${row.id}`}
          name="fullName"
          placeholder="Add tracking number..."
          onChange={onTrackingNumberChange(row.id)}
          value={missingTrackingNumbers[row.id] || ''}
          containerSX={{ flex: 1 }}
          removeSpacer
        />
      );
    }
    if (row.trackingNumber === '$$ERROR$$' && !isCosMember) {
      return '';
    }
    if (row.trackingNumber === '$$ERROR_NO_TRACKING$$') {
      return 'Provided address has been sent, but is unrecognized by USPS mailstream.';
    }
    return (
      <a
        href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${row.trackingNumber}`}
        style={{ wordBreak: 'break-word', overflowWrap: 'break-word', minWidth: '200px' }}
        target="_blank"
      >
        {row.trackingNumber}
      </a>
    );
  };

  return (
    <>
      <Typography
        style={{
          marginTop: '0.9375rem',
          marginBottom: '0.9375rem',
          fontWeight: '500',
        }}
      >
        Tracking Numbers
      </Typography>
      <BkTable
        renderCell={renderCell}
        renderPagination
        initialRowsPerPage={10}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading}
        page={page}
        data={data}
        columns={columns}
      />
      {isCosMember && (
        <Box sx={{ display: 'flex', gap: '1rem', paddingTop: '1rem' }}>
          <Button type="outline" onClick={onReset}>
            Discard Changes
          </Button>
          <Button type="primary" onClick={onSubmit}>
            Submit Tracking Numbers
          </Button>
        </Box>
      )}
    </>
  );
};

export default JobTrackingNumbers;
